interface SectionHeadingProps {
  title: string;
  description?: string;
  eyebrow?: string;
  align?: "left" | "center";
  onDark?: boolean;
  // "sans" swaps the display typeface for the site's regular sans font, at a
  // smaller, weight-driven size instead of the display face's own scale —
  // used where a heading needs to read as clean/modern rather than
  // editorial (e.g. the account page), without touching every other
  // section's heading.
  variant?: "display" | "sans";
  // Only meaningful for variant="sans" — "md" (default) is for a page-level
  // title, "sm" for a subsection heading beneath it, so the two can sit in
  // a clear size hierarchy instead of reading as the same level.
  size?: "sm" | "md";
}

const SANS_SIZE_CLASS: Record<"sm" | "md", string> = {
  sm: "text-sm md:text-base",
  md: "text-base md:text-lg",
};

export default function SectionHeading({
  title,
  description,
  eyebrow,
  align = "left",
  onDark = false,
  variant = "display",
  size = "md",
}: SectionHeadingProps) {
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      {eyebrow && (
        <p className={`eyebrow mb-3 ${onDark ? "text-white/70" : "text-accent"}`}>{eyebrow}</p>
      )}
      <h2
        className={
          variant === "sans"
            ? `font-sans font-semibold tracking-tight ${SANS_SIZE_CLASS[size]} ${
                onDark ? "text-white" : "text-ink"
              }`
            : `font-display uppercase text-[40px] font-medium leading-[1.1] tracking-[0.3px] md:text-[45px] ${
                onDark ? "text-white" : "text-ink"
              }`
        }
      >
        {title}
      </h2>
      {description && (
        <p
          className={`mt-4 text-sm leading-relaxed ${
            onDark ? "text-white/70" : "text-steel-600"
          }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}
